import React from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';

interface SkeletonProps {
  className?: string;
}

export const Skeleton: React.FC<SkeletonProps> = ({ className = '' }) => {
  return (
    <div className={`animate-pulse bg-muted rounded ${className}`} />
  );
};

export const TableSkeleton: React.FC = () => {
  return (
    <div className="space-y-3">
      {Array.from({ length: 5 }).map((_, index) => (
        <div key={index} className="flex items-center space-x-4 p-4">
          <Skeleton className="h-10 w-10 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-1/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
          <Skeleton className="h-6 w-16" />
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-8 w-8 rounded" />
        </div>
      ))}
    </div>
  );
};

export const CardSkeleton: React.FC = () => {
  return (
    <Card className="p-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-4 w-4" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-8 w-16" />
          <Skeleton className="h-4 w-24" />
        </div>
      </div>
    </Card>
  );
};

export const DashboardSkeleton: React.FC = () => {
  return (
    <div className="space-y-4 md:space-y-6">
      {/* Welcome Section */}
      <div>
        <Skeleton className="h-7 sm:h-8 w-48 sm:w-64" />
        <div className="flex flex-col gap-1 mt-2">
          <Skeleton className="h-4 sm:h-5 w-64 sm:w-80" />
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="space-y-3 md:space-y-4">
        {/* Ticket Status Section */}
        <div>
          <Skeleton className="h-5 sm:h-6 w-28 mb-2 sm:mb-3" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <Card key={i} className="border-0 border-l-4 border-l-muted">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-4" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-8 w-12 mb-1" />
                  <Skeleton className="h-3 w-24" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Key Metrics Section */}
        <div>
          <Skeleton className="h-5 sm:h-6 w-24 mb-2 sm:mb-3" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="border-0 border-l-4 border-l-muted">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-4" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-8 w-12 mb-1" />
                  <Skeleton className="h-3 w-32" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {Array.from({ length: 2 }).map((_, i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-5 sm:h-6 w-40" />
              <Skeleton className="h-3 sm:h-4 w-56 mt-1" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-[250px] sm:h-[300px] w-full" />
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 items-start">
        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <Skeleton className="h-5 sm:h-6 w-32" />
                <Skeleton className="h-3 sm:h-4 w-48 mt-1" />
              </div>
              <Skeleton className="h-4 w-4" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 sm:space-y-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-start gap-3">
                  <Skeleton className="h-7 w-7 rounded-md" />
                  <div className="flex-1 space-y-1">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                </div>
              ))}
            </div>
            <Skeleton className="h-10 w-full mt-4" />
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <Skeleton className="h-5 sm:h-6 w-28" />
            <Skeleton className="h-3 sm:h-4 w-44 mt-1" />
          </CardHeader>
          <CardContent className="space-y-3 sm:space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Dashboard data section skeleton (without header) - for use when filtering
export const DashboardDataSkeleton: React.FC = () => {
  return (
    <>
      {/* Metrics Cards */}
      <div className="space-y-3 md:space-y-4">
        {/* Ticket Status Section */}
        <div>
          <Skeleton className="h-5 sm:h-6 w-28 mb-2 sm:mb-3" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <Card key={i} className="border-0 border-l-4 border-l-muted">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-4" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-8 w-12 mb-1" />
                  <Skeleton className="h-3 w-24" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Key Metrics Section */}
        <div>
          <Skeleton className="h-5 sm:h-6 w-24 mb-2 sm:mb-3" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="border-0 border-l-4 border-l-muted">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-4" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-8 w-12 mb-1" />
                  <Skeleton className="h-3 w-32" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {Array.from({ length: 2 }).map((_, i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-5 sm:h-6 w-40" />
              <Skeleton className="h-3 sm:h-4 w-56 mt-1" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-[250px] sm:h-[300px] w-full" />
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 items-start">
        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <Skeleton className="h-5 sm:h-6 w-32" />
                <Skeleton className="h-3 sm:h-4 w-48 mt-1" />
              </div>
              <Skeleton className="h-4 w-4" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 sm:space-y-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-start gap-3">
                  <Skeleton className="h-7 w-7 rounded-md" />
                  <div className="flex-1 space-y-1">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                </div>
              ))}
            </div>
            <Skeleton className="h-10 w-full mt-4" />
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <Skeleton className="h-5 sm:h-6 w-28" />
            <Skeleton className="h-3 sm:h-4 w-44 mt-1" />
          </CardHeader>
          <CardContent className="space-y-3 sm:space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export const TicketListSkeleton: React.FC = () => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="space-y-2">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-4 w-48" />
        </div>
        <Skeleton className="h-10 w-32" />
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Skeleton className="h-4 w-16" />
            <div className="flex">
              <Skeleton className="h-10 flex-1 rounded-r-none" />
              <Skeleton className="h-10 w-20 rounded-l-none" />
            </div>
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-10 w-full" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-10 w-full" />
          </div>
          <div className="flex items-end">
            <Skeleton className="h-10 w-full" />
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card>
        <div className="p-4 border-b">
          <div className="grid grid-cols-8 gap-4">
            {Array.from({ length: 8 }).map((_, index) => (
              <Skeleton key={index} className="h-4 w-full" />
            ))}
          </div>
        </div>
        <TableSkeleton />
      </Card>
    </div>
  );
};

export const CustomerListSkeleton: React.FC = () => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-10 w-32" />
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <CardSkeleton key={index} />
        ))}
      </div>

      {/* Customer Directory */}
      <Card>
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-4 w-32" />
            </div>
            <div className="flex items-center gap-2">
              <Skeleton className="h-10 w-80" />
              <Skeleton className="h-10 w-10" />
            </div>
          </div>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            <div className="grid grid-cols-6 gap-4 pb-2 border-b">
              {Array.from({ length: 6 }).map((_, index) => (
                <Skeleton key={index} className="h-4 w-full" />
              ))}
            </div>
            <TableSkeleton />
          </div>
        </div>
      </Card>
    </div>
  );
};

export const ArticleListSkeleton: React.FC = () => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-10 w-32" />
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {Array.from({ length: 5 }).map((_, index) => (
          <CardSkeleton key={index} />
        ))}
      </div>

      {/* Popular Articles Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-5" />
              <Skeleton className="h-6 w-48" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="flex items-start gap-3 p-3">
                  <Skeleton className="h-6 w-6 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-3 w-3" />
                      <Skeleton className="h-3 w-16" />
                      <Skeleton className="h-3 w-2" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-5" />
              <Skeleton className="h-6 w-40" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="flex items-start gap-3 p-3">
                  <Skeleton className="h-4 w-4" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-5 w-16 rounded-full" />
                      <Skeleton className="h-3 w-2" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="space-y-2">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="space-y-2">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
            <div className="flex items-end">
              <Skeleton className="h-10 w-full" />
            </div>
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card>
        <div className="overflow-x-auto">
          <div className="p-4 border-b">
            <div className="grid grid-cols-7 gap-4">
              {Array.from({ length: 7 }).map((_, index) => (
                <Skeleton key={index} className="h-4 w-full" />
              ))}
            </div>
          </div>
          <TableSkeleton />
        </div>
      </Card>
    </div>
  );
};

export const UserListSkeleton: React.FC = () => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-10 w-32" />
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Array.from({ length: 2 }).map((_, index) => (
          <CardSkeleton key={index} />
        ))}
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="space-y-2">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card>
        <div className="overflow-x-auto">
          <div className="p-4 border-b">
            <div className="grid grid-cols-6 gap-4">
              {Array.from({ length: 6 }).map((_, index) => (
                <Skeleton key={index} className="h-4 w-full" />
              ))}
            </div>
          </div>
          <TableSkeleton />
        </div>
      </Card>
    </div>
  );
};

export const TicketDetailSkeleton: React.FC = () => {
  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 border-b border-gray-200 dark:border-gray-700 pb-3 sm:pb-4">
        <div className="flex items-start sm:items-center gap-3 sm:gap-4 w-full sm:w-auto">
          <Skeleton className="h-10 w-10 flex-shrink-0" />
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-6 sm:h-8 w-full max-w-md" />
            <Skeleton className="h-3 sm:h-4 w-48" />
          </div>
        </div>
        <Skeleton className="h-10 w-28 sm:w-32" />
      </div>

      {/* Progress Bar */}
      <div className="flex justify-center py-2">
        <Skeleton className="h-8 w-full max-w-2xl" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-4 md:space-y-6">
          {/* Ticket Details Card */}
          <Card className="p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <Skeleton className="h-6 w-32 mb-4" />
            <div className="space-y-4">
              <div className="space-y-2">
                <Skeleton className="h-4 w-40" />
                <div className="flex items-center gap-3">
                  <Skeleton className="h-6 w-24" />
                  <Skeleton className="h-4 w-2" />
                  <Skeleton className="h-6 w-48" />
                </div>
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-24 w-full" />
              </div>
            </div>
          </Card>

          {/* Comments Section */}
          <Card className="p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <Skeleton className="h-5 sm:h-6 w-28 mb-3 sm:mb-4" />
            <div className="max-h-[300px] sm:max-h-96 border rounded-md p-3 sm:p-4 bg-muted/20 space-y-4">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="border-l-4 border-muted pl-2 sm:pl-4 py-2 sm:py-3 rounded-r-md bg-muted/30">
                  <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-start mb-2">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-3 w-32" />
                    </div>
                    <Skeleton className="h-3 w-28" />
                  </div>
                  <Skeleton className="h-12 w-full" />
                </div>
              ))}
            </div>
            {/* Add Comment Section */}
            <div className="mt-6 pt-6 border-t border-border">
              <Skeleton className="h-5 w-28 mb-3" />
              <Skeleton className="h-24 w-full mb-3" />
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <Skeleton className="h-4 w-64" />
                <Skeleton className="h-10 w-full sm:w-32" />
              </div>
            </div>
          </Card>

          {/* Ticket History */}
          <Card className="p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <Skeleton className="h-6 w-32 mb-4" />
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="flex items-start gap-3">
                  <Skeleton className="h-8 w-8 rounded-full flex-shrink-0" />
                  <div className="flex-1 space-y-1">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4 md:space-y-6">
          {/* Ticket Information Card */}
          <Card className="p-3 sm:p-4 shadow-sm border border-gray-200 dark:border-gray-700">
            <Skeleton className="h-6 w-40 mb-4" />
            <div className="space-y-4">
              {/* Status */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Skeleton className="w-8 h-8 rounded-lg" />
                  <Skeleton className="h-4 w-12" />
                </div>
                <div className="ml-10">
                  <Skeleton className="h-6 w-20 rounded-full" />
                </div>
              </div>
              {/* Priority */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Skeleton className="w-8 h-8 rounded-lg" />
                  <Skeleton className="h-4 w-14" />
                </div>
                <div className="ml-10">
                  <Skeleton className="h-6 w-16 rounded-full" />
                </div>
              </div>
              {/* Category */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Skeleton className="w-8 h-8 rounded-lg" />
                  <Skeleton className="h-4 w-16" />
                </div>
                <Skeleton className="ml-10 h-4 w-24" />
              </div>
              {/* Created */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Skeleton className="w-8 h-8 rounded-lg" />
                  <Skeleton className="h-4 w-14" />
                </div>
                <Skeleton className="ml-10 h-4 w-32" />
              </div>
              {/* Last Updated */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Skeleton className="w-8 h-8 rounded-lg" />
                  <Skeleton className="h-4 w-24" />
                </div>
                <Skeleton className="ml-10 h-4 w-32" />
              </div>
            </div>
          </Card>

          {/* Assignment Card */}
          <Card className="p-3 sm:p-4 shadow-sm border border-gray-200 dark:border-gray-700">
            <Skeleton className="h-6 w-24 mb-4" />
            <div className="flex items-center gap-2 mb-1">
              <Skeleton className="w-8 h-8 rounded-lg" />
              <Skeleton className="h-4 w-20" />
            </div>
            <Skeleton className="ml-10 h-4 w-28" />
          </Card>

          {/* Customer Card with Participants */}
          <Card className="p-3 sm:p-4 shadow-sm border border-gray-200 dark:border-gray-700">
            <Skeleton className="h-6 w-20 mb-4" />
            <div className="space-y-4">
              {/* Primary Customer */}
              <div className="flex items-start gap-3">
                <Skeleton className="w-8 h-8 rounded-lg flex-shrink-0" />
                <div className="min-w-0 space-y-1">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-40" />
                </div>
              </div>
              {/* Participants Section */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-8 w-24" />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Skeleton className="w-6 h-6 rounded-full" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Attachments Card */}
          <Card className="p-3 sm:p-4 shadow-sm border border-gray-200 dark:border-gray-700">
            <Skeleton className="h-6 w-28 mb-4" />
            <div className="space-y-2">
              {Array.from({ length: 2 }).map((_, index) => (
                <div key={index} className="flex items-center gap-3 p-2">
                  <Skeleton className="w-8 h-8 rounded-lg flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-24 mt-1" />
                  </div>
                  <Skeleton className="h-4 w-4" />
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export const CustomerDetailSkeleton: React.FC = () => {
  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-3 sm:gap-4 w-full sm:w-auto">
          <Skeleton className="h-10 w-10 flex-shrink-0" />
          <div className="min-w-0 space-y-2">
            <Skeleton className="h-6 sm:h-8 w-48 sm:w-64" />
            <Skeleton className="h-4 sm:h-5 w-40 sm:w-48" />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
          <Skeleton className="h-10 w-full sm:w-32" />
          <Skeleton className="h-10 w-full sm:w-36" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer Info Card */}
          <Card className="p-6">
            <div className="space-y-4">
              <Skeleton className="h-6 w-40" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div key={index} className="space-y-2">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-5 w-full" />
                  </div>
                ))}
              </div>
            </div>
          </Card>

          {/* Recent Tickets */}
          <Card className="p-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Skeleton className="h-6 w-32" />
                <Skeleton className="h-4 w-16" />
              </div>
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div key={index} className="flex items-center gap-4 p-4 border rounded">
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-3/4" />
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-3 w-16" />
                        <Skeleton className="h-5 w-20 rounded-full" />
                      </div>
                    </div>
                    <Skeleton className="h-4 w-24" />
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <Card className="p-4">
            <div className="space-y-4">
              <Skeleton className="h-5 w-24" />
              <div className="space-y-3">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className="flex justify-between">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-4 w-8" />
                  </div>
                ))}
              </div>
            </div>
          </Card>

          {/* Actions */}
          <Card className="p-4">
            <div className="space-y-4">
              <Skeleton className="h-5 w-16" />
              <div className="space-y-2">
                <Skeleton className="h-9 w-full" />
                <Skeleton className="h-9 w-full" />
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export const UserDetailSkeleton: React.FC = () => {
  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-3 sm:gap-4 w-full sm:w-auto">
          <Skeleton className="h-10 w-10 flex-shrink-0" />
          <div className="min-w-0 space-y-2">
            <Skeleton className="h-6 sm:h-8 w-40" />
            <Skeleton className="h-4 sm:h-5 w-48" />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
          <Skeleton className="h-10 w-full sm:w-20" />
          <Skeleton className="h-10 w-full sm:w-44" />
        </div>
      </div>

      {/* Single Card (no sidebar - matches actual UserDetail layout) */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <Card className="p-4 sm:p-6">
          <Skeleton className="h-5 sm:h-6 w-36 mb-3 sm:mb-4" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-5 w-full" />
                <Skeleton className="h-3 w-48" />
              </div>
            ))}
          </div>

          {/* Roles Section */}
          <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t">
            <Skeleton className="h-4 sm:h-5 w-36 mb-2 sm:mb-3" />
            <Skeleton className="h-4 w-64 mb-3" />
            <div className="flex flex-wrap gap-2">
              <Skeleton className="h-6 w-16 rounded-full" />
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

// Customer Portal Skeletons

export const CustomerPortalTicketsSkeleton: React.FC = () => {
  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Welcome Header */}
      <Card>
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-center sm:items-center justify-between gap-4">
            <div className="flex flex-col sm:flex-row items-center sm:items-center gap-3 sm:gap-4 flex-1">
              <Skeleton className="w-20 h-20 sm:w-24 sm:h-24 md:w-32 md:h-32 flex-shrink-0" />
              <div className="text-center sm:text-left">
                <Skeleton className="h-6 sm:h-8 md:h-10 w-64 sm:w-80 md:w-96 mx-auto sm:mx-0" />
              </div>
            </div>
            <Skeleton className="h-12 w-36 flex-shrink-0" />
          </div>
        </CardContent>
      </Card>

      {/* Dashboard Section */}
      <div className="space-y-4">
        {/* Stats Cards Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <Skeleton className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <Skeleton className="h-6 sm:h-8 w-12 mb-1" />
                  <Skeleton className="h-3 sm:h-4 w-16 sm:w-20" />
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Expand/Collapse Button */}
        <div className="flex justify-center">
          <Skeleton className="h-9 w-48" />
        </div>
      </div>

      {/* Ticket Management Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-5" />
            <Skeleton className="h-6 w-32" />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search and Filters */}
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            {/* Search */}
            <Skeleton className="h-10 w-full md:max-w-md" />
            {/* Filters */}
            <div className="flex flex-wrap gap-2 sm:gap-3">
              <Skeleton className="h-10 w-full md:w-[200px]" />
              <Skeleton className="h-10 w-full md:w-[200px]" />
              <Skeleton className="h-10 w-10" />
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 dark:border-gray-700 my-4" />

          {/* Tickets List */}
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <Card key={i} className="border-l-4 border-muted">
                <CardContent className="p-0">
                  {/* Header Section */}
                  <div className="p-3 sm:p-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between gap-6">
                      {/* Left side: Title and Badges */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <Skeleton className="h-5 sm:h-6 w-16" />
                          <Skeleton className="h-4 w-2" />
                          <Skeleton className="h-5 sm:h-6 w-48" />
                        </div>
                        {/* Status and Priority Badges */}
                        <div className="flex flex-wrap gap-1.5">
                          <Skeleton className="h-6 w-20 rounded-md" />
                          <Skeleton className="h-6 w-16 rounded-md" />
                        </div>
                      </div>
                      {/* Right side: Progress Bar */}
                      <div className="flex-shrink-0 hidden sm:flex">
                        <Skeleton className="h-8 w-48" />
                      </div>
                    </div>
                  </div>

                  {/* Mobile Progress Bar Section */}
                  <div className="sm:hidden px-3 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-center">
                    <Skeleton className="h-8 w-48" />
                  </div>

                  {/* Expandable Section - Description and Metadata */}
                  <div className="md:hidden">
                    {/* Description Section */}
                    <div className="px-3 sm:px-4 py-2.5 bg-gray-50 dark:bg-gray-800/40 border-b border-gray-200 dark:border-gray-700">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-3/4 mt-1" />
                    </div>

                    {/* Metadata Footer */}
                    <div className="p-3 sm:p-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-2">
                        {Array.from({ length: 4 }).map((_, j) => (
                          <div key={j} className="flex items-center gap-2">
                            <Skeleton className="w-7 h-7 rounded-md flex-shrink-0" />
                            <div className="min-w-0">
                              <Skeleton className="h-3 w-16 mb-1" />
                              <Skeleton className="h-3 w-20" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          <div className="bg-background px-3 sm:px-6 py-3 sm:py-4 border-t border-border rounded-b-lg mt-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-2 sm:gap-4">
                <Skeleton className="h-4 w-12" />
                <Skeleton className="h-8 w-16" />
                <Skeleton className="h-4 w-16" />
              </div>
              <Skeleton className="h-4 w-40" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const CustomerPortalTicketDetailSkeleton: React.FC = () => {
  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 border-b border-gray-200 dark:border-gray-700 pb-3 sm:pb-4 shadow-sm">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 w-full sm:w-auto">
          <Skeleton className="h-9 w-32" />
          <div className="min-w-0 space-y-1">
            <Skeleton className="h-6 sm:h-8 w-full max-w-sm" />
            <Skeleton className="h-3 sm:h-4 w-24" />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <Skeleton className="h-9 w-28" />
          <Skeleton className="h-9 w-36" />
        </div>
      </div>

      {/* Progress Bar */}
      <div className="flex justify-center py-2">
        <Skeleton className="h-8 w-full max-w-2xl" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-4 sm:space-y-6">
          {/* Ticket Details */}
          <Card className="p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <Skeleton className="h-5 sm:h-6 w-32 mb-3 sm:mb-4" />
            <div className="space-y-4">
              <div className="space-y-2">
                <Skeleton className="h-4 w-40" />
                <div className="flex items-center gap-3">
                  <Skeleton className="h-5 sm:h-6 w-24" />
                  <Skeleton className="h-4 w-2" />
                  <Skeleton className="h-5 sm:h-6 w-48" />
                </div>
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-24 w-full" />
              </div>
            </div>
          </Card>

          {/* Comments Section */}
          <Card className="p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <Skeleton className="h-5 sm:h-6 w-28 mb-3 sm:mb-4" />
            <div className="max-h-96 border rounded-md p-3 sm:p-4 bg-muted/20 space-y-4">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="border-l-4 border-muted pl-4 py-3 rounded-r-md bg-muted/30 shadow-sm">
                  <div className="flex flex-wrap justify-between items-start gap-2 mb-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Skeleton className="h-4 sm:h-5 w-24" />
                      <Skeleton className="h-3 sm:h-4 w-32" />
                    </div>
                    <Skeleton className="h-3 sm:h-4 w-28 flex-shrink-0" />
                  </div>
                  <Skeleton className="h-12 w-full" />
                </div>
              ))}
            </div>
            {/* Add Comment Section */}
            <div className="mt-6 pt-6 border-t border-border">
              <Skeleton className="h-4 sm:h-5 w-28 mb-3" />
              <Skeleton className="h-24 w-full mb-3" />
              <div className="flex justify-end">
                <Skeleton className="h-10 w-32" />
              </div>
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4 sm:space-y-6">
          {/* Ticket Information */}
          <Card className="shadow-sm border border-gray-200 dark:border-gray-700">
            <CardHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3">
              <Skeleton className="h-5 sm:h-6 w-40" />
            </CardHeader>
            <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6 pt-0">
              <div className="space-y-4">
                {/* Status */}
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Skeleton className="w-8 h-8 rounded-lg" />
                    <Skeleton className="h-4 w-12" />
                  </div>
                  <div className="ml-10">
                    <Skeleton className="h-6 w-20 rounded-full" />
                  </div>
                </div>
                {/* Priority */}
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Skeleton className="w-8 h-8 rounded-lg" />
                    <Skeleton className="h-4 w-14" />
                  </div>
                  <div className="ml-10">
                    <Skeleton className="h-6 w-16 rounded-full" />
                  </div>
                </div>
                {/* Category */}
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Skeleton className="w-8 h-8 rounded-lg" />
                    <Skeleton className="h-4 w-16" />
                  </div>
                  <Skeleton className="ml-10 h-4 w-24" />
                </div>
                {/* Assigned To */}
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Skeleton className="w-8 h-8 rounded-lg" />
                    <Skeleton className="h-4 w-20" />
                  </div>
                  <Skeleton className="ml-10 h-4 w-28" />
                </div>
                {/* Created */}
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Skeleton className="w-8 h-8 rounded-lg" />
                    <Skeleton className="h-4 w-14" />
                  </div>
                  <Skeleton className="ml-10 h-4 w-28" />
                </div>
                {/* Last Updated */}
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Skeleton className="w-8 h-8 rounded-lg" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                  <Skeleton className="ml-10 h-4 w-32" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Attachments */}
          <Card className="p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <Skeleton className="h-5 sm:h-6 w-28 mb-3 sm:mb-4" />
            <div className="space-y-2">
              {Array.from({ length: 2 }).map((_, index) => (
                <div key={index} className="flex items-center gap-3 p-2">
                  <Skeleton className="w-8 h-8 rounded-lg flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-20 mt-1" />
                  </div>
                  <Skeleton className="h-4 w-4" />
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export const CustomerPortalKnowledgeBaseSkeleton: React.FC = () => {
  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
        <div className="px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <Skeleton className="h-16 w-16 rounded-full mx-auto mb-6" />
            <Skeleton className="h-8 w-80 mx-auto mb-4" />
            <Skeleton className="h-5 w-96 mx-auto mb-8" />
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <Skeleton className="h-12 w-full rounded-lg" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        {/* Categories */}
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-6">
            <Skeleton className="h-8 w-48" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <div className="space-y-3">
                    <Skeleton className="h-6 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-5 w-20 rounded-full" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-8">
          {/* Popular Articles */}
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                <Skeleton className="h-5 w-5" />
                <Skeleton className="h-6 w-32" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <Skeleton className="h-6 w-6 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-full" />
                      <div className="flex items-center space-x-4">
                        <Skeleton className="h-3 w-12" />
                        <Skeleton className="h-3 w-16" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Need More Help */}
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                <Skeleton className="h-5 w-5" />
                <Skeleton className="h-6 w-32" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Skeleton className="h-5 w-48 mb-2" />
                  <Skeleton className="h-4 w-full mb-3" />
                  <Skeleton className="h-10 w-full" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export const CustomerPortalKnowledgeBaseArticleSkeleton: React.FC = () => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Skeleton className="h-10 w-40" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Article Content */}
        <div className="lg:col-span-3">
          <Card>
            <CardHeader>
              <div className="space-y-2">
                <Skeleton className="h-5 w-24 rounded-full" />
                <Skeleton className="h-8 w-3/4" />
                <div className="flex items-center space-x-4">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-24" />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Summary */}
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <Skeleton className="h-5 w-full" />
                </div>
                
                {/* Content */}
                <div className="space-y-4">
                  <Skeleton className="h-6 w-48" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-6 w-40" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <Skeleton className="h-5 w-32" />
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="flex items-start space-x-2">
                    <Skeleton className="h-4 w-4" />
                    <Skeleton className="h-4 w-full" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export const CategorySettingsSkeleton: React.FC = () => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-80" />
        </div>
        <Skeleton className="h-10 w-32" />
      </div>

      {/* Search Bar */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-10 w-80" />
        </div>

        {/* Categories Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-5" />
              <Skeleton className="h-6 w-40" />
            </div>
          </CardHeader>
          <CardContent>
            {/* Table Header */}
            <div className="space-y-3">
              <div className="grid grid-cols-4 gap-4 pb-2 border-b">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-20" />
              </div>
              
              {/* Table Rows */}
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="grid grid-cols-4 gap-4 items-center py-3">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-3 w-32" />
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-5 w-14 rounded-full" />
                    <Skeleton className="h-5 w-12 rounded-full" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-8 w-8 rounded" />
                    <Skeleton className="h-8 w-8 rounded" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const UserSettingsSkeleton: React.FC = () => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-4 w-72" />
        </div>
        <Skeleton className="h-10 w-28" />
      </div>

      {/* Stats */}
      <div className="flex gap-2">
        <Skeleton className="h-6 w-24 rounded-full" />
        <Skeleton className="h-6 w-20 rounded-full" />
      </div>

      {/* Search Bar */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-10 w-80" />
        </div>

        {/* Users Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-5" />
              <Skeleton className="h-6 w-32" />
            </div>
          </CardHeader>
          <CardContent>
            {/* Table Header */}
            <div className="space-y-3">
              <div className="grid grid-cols-6 gap-4 pb-2 border-b">
                <Skeleton className="h-4 w-12" />
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-12" />
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-16" />
              </div>

              {/* Table Rows */}
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="grid grid-cols-6 gap-4 items-center py-3">
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-8 w-8 rounded-full" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                  <Skeleton className="h-3 w-32" />
                  <Skeleton className="h-5 w-14 rounded-full" />
                  <Skeleton className="h-5 w-14 rounded-full" />
                  <Skeleton className="h-3 w-24" />
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-8 w-8 rounded" />
                    <Skeleton className="h-8 w-8 rounded" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const ProfileSettingsSkeleton: React.FC = () => {
  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      {/* Header */}
      <div className="mb-8">
        <Skeleton className="h-8 w-48 mb-2" />
        <Skeleton className="h-5 w-80" />
      </div>

      {/* Main Card */}
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-40 mb-2" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-10 w-full" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-10 w-full" />
              </div>
            </div>

            {/* Email (read-only) */}
            <div className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-3 w-96" />
            </div>

            {/* Separator */}
            <div className="border-t border-border" />

            {/* Contact Information Section */}
            <div className="space-y-4">
              <Skeleton className="h-5 w-40" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-10 w-full" />
              </div>
            </div>

            {/* Separator */}
            <div className="border-t border-border" />

            {/* Professional Information Section */}
            <div className="space-y-4">
              <Skeleton className="h-5 w-48" />
              <div className="space-y-4">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-10 w-full" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-10 w-full" />
                </div>
              </div>
            </div>

            {/* Separator */}
            <div className="border-t border-border" />

            {/* Preferences Section */}
            <div className="space-y-4">
              <Skeleton className="h-5 w-24" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-10 w-full" />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4 pt-6">
              <Skeleton className="h-10 w-20" />
              <Skeleton className="h-10 w-24" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const SubmitTicketSkeleton: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto py-6 sm:py-8 space-y-4 sm:space-y-6 px-4">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
        {/* Main Form */}
        <div className="lg:col-span-2">
          <Card className="p-4 sm:p-6">
            <div className="space-y-4 sm:space-y-6">
              {/* Page Header */}
              <div className="text-center pb-2">
                <Skeleton className="h-6 sm:h-8 w-48 mx-auto" />
                <Skeleton className="h-4 sm:h-5 w-40 mx-auto mt-2" />
              </div>

              {/* Subject Input */}
              <div className="space-y-2">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-10 w-full" />
              </div>

              {/* Priority and Category Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-14" />
                  <Skeleton className="h-10 w-full" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-10 w-full" />
                </div>
              </div>

              {/* Description Textarea */}
              <div className="space-y-2">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-32 w-full" />
              </div>

              {/* Attachments Section */}
              <div>
                <Skeleton className="h-5 w-24 mb-3" />
                <Skeleton className="h-24 w-full rounded-lg" />
              </div>

              {/* Submit Buttons */}
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex flex-col sm:flex-row gap-3">
                  <Skeleton className="h-10 w-full sm:flex-1" />
                  <Skeleton className="h-10 w-full sm:w-24" />
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4 sm:space-y-6">
          <Card>
            <CardHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3">
              <Skeleton className="h-4 sm:h-5 w-32" />
            </CardHeader>
            <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6 pt-0">
              <div className="space-y-4 text-sm">
                {/* Knowledge Base Section */}
                <div>
                  <Skeleton className="h-4 w-40 mb-2" />
                  <Skeleton className="h-3 w-full mb-1" />
                  <Skeleton className="h-3 w-3/4 mb-3" />
                  <Skeleton className="h-9 w-full" />
                </div>

                {/* Tips Section */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <Skeleton className="h-4 w-36 mb-2" />
                  <div className="space-y-1">
                    <Skeleton className="h-3 w-40" />
                    <Skeleton className="h-3 w-36" />
                    <Skeleton className="h-3 w-44" />
                    <Skeleton className="h-3 w-40" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export const OrganizationSettingsSkeleton: React.FC = () => {
  return (
    <div className="space-y-4 md:space-y-6">
      {/* Organization Information Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-5" />
            <Skeleton className="h-6 w-48" />
          </div>
          <Skeleton className="h-4 w-72" />
        </CardHeader>
        <CardContent className="space-y-3 sm:space-y-4 p-4 sm:p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            <div className="space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-5 w-40" />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-5 w-32" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Email Settings Card (Collapsible - shown collapsed) */}
      <Card>
        <CardHeader className="cursor-pointer">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-5" />
              <Skeleton className="h-6 w-28" />
            </div>
            <Skeleton className="h-5 w-5" />
          </div>
          <Skeleton className="h-4 w-80" />
        </CardHeader>
      </Card>

      {/* Ticket Reminder Notifications Card (Collapsible - shown collapsed) */}
      <Card>
        <CardHeader className="cursor-pointer">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-5" />
              <Skeleton className="h-6 w-52" />
            </div>
            <Skeleton className="h-5 w-5" />
          </div>
          <Skeleton className="h-4 w-48" />
        </CardHeader>
      </Card>

      {/* Customer Portal Card (Collapsible - shown collapsed) */}
      <Card>
        <CardHeader className="cursor-pointer">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-5" />
              <Skeleton className="h-6 w-32" />
            </div>
            <Skeleton className="h-5 w-5" />
          </div>
          <Skeleton className="h-4 w-72" />
        </CardHeader>
      </Card>

      {/* Ticket Settings Card (Collapsible - shown collapsed) */}
      <Card>
        <CardHeader className="cursor-pointer">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-5" />
              <Skeleton className="h-6 w-28" />
            </div>
            <Skeleton className="h-5 w-5" />
          </div>
          <Skeleton className="h-4 w-56" />
        </CardHeader>
      </Card>

      {/* Business Hours & SLA Settings Card (Collapsible - shown collapsed) */}
      <Card>
        <CardHeader className="cursor-pointer">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-5" />
              <Skeleton className="h-6 w-56" />
            </div>
            <Skeleton className="h-5 w-5" />
          </div>
          <Skeleton className="h-4 w-80" />
        </CardHeader>
      </Card>

      {/* Save/Cancel Actions Card */}
      <Card>
        <CardContent className="pt-6 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Skeleton className="w-2 h-2 rounded-full" />
              <Skeleton className="h-4 w-40" />
            </div>
            <div className="flex flex-col-reverse sm:flex-row-reverse items-stretch sm:items-center gap-2 w-full sm:w-auto">
              <Skeleton className="h-10 w-full sm:w-20" />
              <Skeleton className="h-10 w-full sm:w-28" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};